import { Modes, freeRoutes } from "@highmarch/app";
import { Rarity } from "@highmarch/domain";
import { toast } from "../ui/toast";
import { showFreeRewardsSheet } from "../ui/forge/freeRewardsSheet";
import { showEndlessSheet } from "../ui/modes/endlessSheet";
import { eventClock } from "./events";
import { Feature, HubApi, MORE_ORDER } from "./registry";

/** The Free rewards row sits just under Events and Endless on the More sheet. */
const FREE_ORDER = MORE_ORDER.events + 3;
/** Free routes open with the first of Iron (a 3-star level) or Endless (its unlock level). */

/**
 * Gear v2's player-facing extras outside the Armoury (docs/GEAR_V2.md §4): More → Free rewards (the Endless weekly
 * milestones and the Iron star totals with CLAIM, the forge purse, the other free routes) whose red count is the one
 * "free rewards waiting" badge (`freeRoutes().claimable`, summed into the More tab); the campaign map's Iron chip; the
 * Guide's "Forge & Fusion" topic; the away card's line; and debug hooks for captures. The Armoury's own forge
 * (raise, fusion, feed) is ui/armoury/*.
 */
export const forgeFeature: Feature = {
  id: "forge",
  register(ctx, registry) {
    const modes = new Modes(ctx.catalog);
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    const waiting = () => {
      const r = freeRoutes(ctx.catalog, now());
      return { iron: r.rows.filter((x) => x.source === "iron" && x.reached && !x.claimed).length, endless: r.rows.filter((x) => x.source === "endless" && x.reached && !x.claimed).length };
    };
    const routesOpen = () => modes.endlessOpen || modes.ironUnlocked || ctx.catalog.modes.ironStarCount > 0;
    const openEndless = (parent: HTMLElement, changed: () => void) => {
      modes.pendingChest(now());
      showEndlessSheet(parent, { catalog: ctx.catalog, modes, now, onChange: changed, start: () => registry.startRun(modes.endlessLevel, modes.endlessRun()) });
    };
    const open = (parent: HTMLElement, changed: () => void) => showFreeRewardsSheet(parent, {
      catalog: ctx.catalog, modes, now, onChange: changed,
      openEndless: modes.endlessOpen ? () => openEndless(parent, changed) : undefined,
    });
    registry.openFreeRewards = (parent) => open(parent, () => ctx.persist());
    registry.freeRewardsWaiting = () => {
      const w = waiting();
      return { ...w, ironStars: ctx.catalog.modes.ironStarCount, ironOpen: modes.ironUnlocked || ctx.catalog.modes.ironStarCount > 0 };
    };

    registry.moreRow({
      id: "freerewards", order: FREE_ORDER, icon: "essence", home: "events",
      label: () => "Free rewards",
      note: () => {
        const w = waiting(), n = w.iron + w.endless;
        return n > 0 ? `${n} ready to claim` : "Essence, sigils, chests";
      },
      dot: () => { const w = waiting(); return w.iron + w.endless; },
      visible: () => routesOpen(),
      open: (hub: HubApi) => { if (routesOpen()) open(hub.parent, () => hub.changed()); else toast(hub.parent, "Free rewards open with Iron and Endless"); },
    });

    registry.guideTopic({
      id: "forge", group: "castle", label: "Forge & Fusion", art: { icon: "flame" }, fromLevel: 2, steps: [],
      lines: [
        "**Forge Essence** raises an item a level, up to its tower's, and every level adds stats. **Get stronger** does it in one tap.",
        "Three items of one rarity **fuse** into one of the next. **Sigils** stand in for missing spares.",
        "Endless milestones, Iron stars, events and achievements pay essence, sigils and chests. Claim them in **Events → Free rewards**.",
      ],
    });

    registry.awayItems(({ awaySeconds }) => {
      if (awaySeconds === null) return [];
      const w = waiting(), n = w.iron + w.endless;
      return n > 0 ? [{ icon: "chest", title: `${n} free reward${n === 1 ? "" : "s"} to claim`, detail: "Endless and Iron milestones · Events → Free rewards" }] : [];
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures and smoke tests: fill the forge, open the sheets.
      (window as unknown as { __tdForge: unknown }).__tdForge = {
        give: (essence: number, sigils: Partial<Record<"common" | "uncommon" | "rare" | "epic" | "legendary", number>> = {}) => {
          const f = ctx.catalog.profile.forge;
          f.essence += essence;
          const ids = { common: Rarity.Common, uncommon: Rarity.Uncommon, rare: Rarity.Rare, epic: Rarity.Epic, legendary: Rarity.Legendary } as const;
          for (const [k, n] of Object.entries(sigils)) f.addSigils(ids[k as keyof typeof ids], n ?? 0);
          ctx.persist();
        },
        openFreeRewards: () => open(ctx.ui, () => ctx.persist()),
        waiting,
      };
    }
  },
};
